import { useRef, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { OverflowMenu, Edit, Settings, Upload, TrashDelete, UserAccount } from '@/assets/icons'
import { Card } from '@/components/card/Card'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { useAppDispatch } from '@/store/store-hooks'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { useNavigate } from 'react-router-dom'
import { kMyDriveName, matterPaths } from '@/routing/paths'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { MatterViewerRole } from '@/matter/store/schemas'
import { canDeleteMatter, canEditMatter, canEditMatterMembers, canEditMatterSettings } from '@/matter/utils/matter-permissions'
import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { IconButton } from '@/components/icon-button/IconButton'

/** Whether to restore focus to the trigger when the menu closes (restore = yes, suppress = no). */
type FocusOnClose = 'restore' | 'suppress'

/**
 * Matter menu actions available for each matter
 */
enum MatterMenuAction {
  Edit = 'edit',
  Delete = 'delete',
  UploadFiles = 'upload-files',
  UploadUserFiles = 'upload-user-files',
  ManageMembers = 'manage-members',
  MatterSettings = 'matter-settings',
}

/**
 * Metadata for each matter menu action
 */
type MatterMenuActionConfig = {
  label: string
  value: MatterMenuAction
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  destructive?: boolean
  disabled?: boolean
}

const matterMenuActionEdit: MatterMenuActionConfig = {
  value: MatterMenuAction.Edit,
  label: 'Edit',
  icon: Edit,
}

const matterMenuActionDelete: MatterMenuActionConfig = {
  value: MatterMenuAction.Delete,
  label: 'Delete',
  icon: TrashDelete,
  destructive: true,
}

const matterMenuActionUploadFiles: MatterMenuActionConfig = {
  value: MatterMenuAction.UploadFiles,
  label: 'Upload Files',
  icon: Upload,
}

const matterMenuActionUploadUserFiles: MatterMenuActionConfig = {
  value: MatterMenuAction.UploadUserFiles,
  label: 'Upload Matter Files',
  icon: Upload,
}

const matterMenuActionManageMembers: MatterMenuActionConfig = {
  value: MatterMenuAction.ManageMembers,
  label: 'Manage Members',
  icon: UserAccount,
}

const matterMenuActionMatterSettings: MatterMenuActionConfig = {
  value: MatterMenuAction.MatterSettings,
  label: 'Matter Settings',
  icon: Settings,
}

type MatterOverflowMenuProps = {
  matterId: string
  viewerRole: MatterViewerRole
  disabled?: boolean
}

/**
 * Matter Overflow Menu
 *
 * Popover based overflow menu for performing actions on a matter.
 */
export default function MatterOverflowMenu(props: MatterOverflowMenuProps) {
  const { matterId, viewerRole, disabled = false } = props

  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const focusOnCloseRef = useRef<FocusOnClose | null>(null)

  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const isAccountCollaborationCapable = useIsAccountCollaborationCapable()
  const subject = { viewer_role: viewerRole }
  const { showUpsell, handleUpsellClick } = useCollaborationUpsell()

  const matterMenuActionsConfig: MatterMenuActionConfig[] = [
    ...(canEditMatter(subject) ? [matterMenuActionEdit] : []),
    // Navigate to the new user-files drive when enabled, otherwise the legacy files drive
    newFilesUxEnabled ? matterMenuActionUploadUserFiles : matterMenuActionUploadFiles,
    ...(driveSyncEnabled && canEditMatterSettings(subject) ? [matterMenuActionMatterSettings] : []),
    ...(isAccountCollaborationCapable && canEditMatterMembers(subject) ? [matterMenuActionManageMembers] : []),
    ...(canDeleteMatter(subject) ? [matterMenuActionDelete] : []),
  ]

  const handleSelect = (action: MatterMenuAction) => {
    focusOnCloseRef.current = 'suppress'
    setOpen(false)
    switch (action) {
      case MatterMenuAction.Edit:
        dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Edit, matterId }))
        break
      case MatterMenuAction.Delete:
        dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Delete, matterId }))
        break
      case MatterMenuAction.UploadFiles:
        navigate(matterPaths.files.folder(matterId, kMyDriveName, matterId))
        break
      case MatterMenuAction.UploadUserFiles:
        navigate(matterPaths.userFiles.root(matterId))
        break
      case MatterMenuAction.ManageMembers:
        dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.ManageMembers, matterId }))
        break
      case MatterMenuAction.MatterSettings:
        navigate(matterPaths.settings(matterId))
        break
      default:
        break
    }
  }

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) focusOnCloseRef.current = null
        setOpen(nextOpen)
      }}
    >
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <IconButton icon={<OverflowMenu />} variant="plain" color="neutral" aria-label="Matter actions" disabled={disabled} />
      </DropdownMenu.Trigger>

      <MenuContent
        side="bottom"
        align="end"
        alignOffset={-3}
        onCloseAutoFocus={(e) => {
          if (focusOnCloseRef.current === 'suppress') e.preventDefault()
        }}
        onInteractOutside={() => {
          focusOnCloseRef.current = 'suppress'
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') focusOnCloseRef.current = 'restore'
        }}
      >
        <MenuItemGroup>
          {matterMenuActionsConfig.map(({ value, label, icon, destructive, disabled }) => (
            <MenuItem
              key={value}
              label={label}
              icon={icon}
              variant={destructive ? 'destructive' : 'neutral'}
              disabled={disabled}
              onSelect={() => handleSelect(value)}
            />
          ))}
        </MenuItemGroup>
        {showUpsell && (
          <>
            <MenuSeparator />
            <DropdownMenu.Item
              onSelect={() => {
                focusOnCloseRef.current = 'suppress'
                handleUpsellClick()
              }}
              className="max-w-[280px] cursor-pointer p-4 focus:outline-none"
            >
              <Card variant="upsell" size="sm" title="Add Team Members" description="Create an organization to collaborate on this matter with colleagues." />
            </DropdownMenu.Item>
          </>
        )}
      </MenuContent>
    </DropdownMenu.Root>
  )
}
